<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { DataTableColumns } from 'naive-ui';

defineOptions({ name: 'MetricsAlertGroupTable' });

const props = withDefaults(
  defineProps<{
    alerts: Api.Auth.MetricsAlert[];
    columns: DataTableColumns<Api.Auth.MetricsAlert>;
    size?: 'small' | 'medium' | 'large';
    striped?: boolean;
    scrollX?: number;
    loading?: boolean;
    pageSize?: number;
  }>(),
  {
    size: 'medium',
    striped: false,
    scrollX: undefined,
    loading: false,
    pageSize: 8
  }
);

function countLevel(alerts: Api.Auth.MetricsAlert[], level: number) {
  return alerts.filter(alert => alert.level === level).length;
}

function getBreachMultiple(alert: Api.Auth.MetricsAlert) {
  const value = alert.breachMultiple;
  return typeof value === 'number' && Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
}

function formatGroupCount(current: number, total: number) {
  return current === total ? `${total} 项` : `本页 ${current} / 共 ${total} 项`;
}

const appGroups = computed(() => {
  const groups = new Map<string, Api.Auth.MetricsAlert[]>();

  props.alerts.forEach(alert => {
    const appName = alert.appName || '-';
    const alerts = groups.get(appName);
    if (alerts) alerts.push(alert);
    else groups.set(appName, [alert]);
  });

  return [...groups.entries()]
    .map(([appName, alerts]) => ({
      appName,
      alerts: [...alerts].sort((left, right) => getBreachMultiple(right) - getBreachMultiple(left))
    }))
    .sort((left, right) => getBreachMultiple(right.alerts[0]) - getBreachMultiple(left.alerts[0]));
});

const expandedApps = ref<string[]>([]);
const page = ref(1);
const sortedAlerts = computed(() => appGroups.value.flatMap(group => group.alerts));
const pageCount = computed(() => Math.max(1, Math.ceil(sortedAlerts.value.length / props.pageSize)));
const pagedAppGroups = computed(() => {
  const start = (page.value - 1) * props.pageSize;
  const pageAlerts = sortedAlerts.value.slice(start, start + props.pageSize);
  const groups = new Map<string, Api.Auth.MetricsAlert[]>();

  pageAlerts.forEach(alert => {
    const appName = alert.appName || '-';
    const alerts = groups.get(appName);
    if (alerts) alerts.push(alert);
    else groups.set(appName, [alert]);
  });

  const allAlertsByApp = new Map(appGroups.value.map(group => [group.appName, group.alerts]));
  return [...groups.entries()].map(([appName, alerts]) => ({
    appName,
    alerts,
    allAlerts: allAlertsByApp.get(appName) ?? alerts
  }));
});

watch(
  appGroups,
  groups => {
    if (!groups.length) {
      page.value = 1;
      expandedApps.value = [];
      return;
    }

    page.value = 1;
  },
  { immediate: true }
);

watch(
  pagedAppGroups,
  groups => {
    expandedApps.value = groups.map(group => group.appName);
  },
  { immediate: true }
);
</script>

<template>
  <NEmpty v-if="appGroups.length === 0" description="暂无数据" />
  <div v-else>
    <NDataTable
      class="metrics-alert-groups__table-header"
      :columns="columns"
      :data="[]"
      :pagination="false"
      :scroll-x="scrollX"
      :bordered="false"
      :single-line="false"
    />
    <NCollapse v-model:expanded-names="expandedApps" class="metrics-alert-groups">
      <NCollapseItem v-for="appGroup in pagedAppGroups" :key="appGroup.appName" :name="appGroup.appName">
        <template #header>
          <span class="metrics-alert-app-group__title">
            <span>
              {{ appGroup.appName }}
              <span class="metrics-alert-group__count">
                （{{ formatGroupCount(appGroup.alerts.length, appGroup.allAlerts.length) }}）
              </span>
            </span>
            <span class="metrics-alert-app-group__summary">
              <span>🔴 严重 {{ countLevel(appGroup.allAlerts, 3) }} 项</span>
              <span>🟠 告警 {{ countLevel(appGroup.allAlerts, 2) }} 项</span>
              <span>🟡 提示 {{ countLevel(appGroup.allAlerts, 1) }} 项</span>
            </span>
          </span>
        </template>

        <div class="metrics-alert-app-group__content">
          <NDataTable
            :columns="columns"
            :data="appGroup.alerts"
            :pagination="false"
            :size="size"
            :striped="striped"
            :scroll-x="scrollX"
            :loading="loading"
            :bordered="false"
            :single-line="false"
          />
        </div>
      </NCollapseItem>
      <div v-if="pageCount > 1" class="metrics-alert-groups__pagination">
        <NPagination v-model:page="page" :page-count="pageCount" :page-slot="7" />
      </div>
    </NCollapse>
  </div>
</template>

<style scoped>
.metrics-alert-groups {
  --n-title-font-weight: 600;
}

.metrics-alert-groups__table-header {
  position: sticky;
  z-index: 2;
  top: 0;
  background: rgb(var(--card-color));
}

.metrics-alert-groups__table-header :deep(.n-data-table-empty) {
  display: none;
}

.metrics-alert-groups :deep(> .n-collapse-item) {
  margin: 0;
  padding: 9px 0;
  border-top: 1px solid rgb(var(--base-text-color) / 0.08);
}

.metrics-alert-groups :deep(> .n-collapse-item:last-child) {
  border-bottom: 1px solid rgb(var(--base-text-color) / 0.08);
}

.metrics-alert-app-group__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  width: 100%;
  font-size: 14px;
}

.metrics-alert-app-group__summary {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  color: rgb(var(--base-text-color) / 0.68);
  font-size: 13px;
}

.metrics-alert-app-group__content {
  padding: 4px 0 2px;
}

.metrics-alert-app-group__content :deep(.n-data-table-thead) {
  display: none;
}

.metrics-alert-group__count {
  color: rgb(var(--base-text-color) / 0.55);
  font-size: 12px;
  font-weight: 400;
}

.metrics-alert-groups__pagination {
  display: flex;
  justify-content: flex-end;
  padding-top: 14px;
}
</style>
